import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";

import { PageHeader } from "@/components/dashboard/page-header";
import { ProfileForm, SecurityPanel } from "@/components/settings/forms";
import { OrgSettingsForm } from "@/components/settings/org-form";
import { IncidentTypesPage } from "@/pages/dashboard/incident-report-types/list";
import { nav, settings as copy } from "@/content";
import { can } from "@/constants/permissions";
import { buildQuery } from "@/utils/query-params";
import { getOwnProfile } from "@/api/settings";
import { listUserSessions } from "@/api/system";
import { cn } from "@/utils";
import { useQuery } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useSession } from "@/context/UserContext";
import { PageError, PageLoading } from "@/components/dashboard/page-state";

const BASE_PATH = "/dashboard/settings";

/**
 * Settings: organisation, profile, security, and (for admins) report types.
 *
 * Tabs rather than three routes, because they are views of one thing and an
 * operator flips between them. The active tab is a search param, so each is
 * linkable and Back works.
 */
export const Route = createFileRoute("/_main/dashboard/settings")({
  component: () => (
    <RequirePermission action="settings.read">
      <SettingsPage />
    </RequirePermission>
  ),
});

function SettingsPage() {
  useDocumentTitle(copy.title);
  const params = useQueryParams();
  const session = useSession();
  const settings = session.settings;

  const canWriteOrg = can(session.user.role, "settings.write");
  const canManageLookups = can(session.user.role, "lookups.manage");
  const requested = typeof params.tab === "string" ? params.tab : undefined;
  const tab =
    requested === "profile" ||
    requested === "security" ||
    (requested === "report-types" && canManageLookups)
      ? requested
      : canWriteOrg
        ? "org"
        : "profile";

  const profileQuery = useQuery({
    queryKey: ["settings", "profile"],
    queryFn: getOwnProfile,
  });
  const sessionsQuery = useQuery({
    queryKey: ["settings", "sessions"],
    queryFn: listUserSessions,
    staleTime: 0,
  });

  if (profileQuery.isPending) return <PageLoading />;
  if (profileQuery.isError || !profileQuery.data) return <PageError error={profileQuery.error} />;

  const profile = profileQuery.data;
  const sessions = sessionsQuery.data ?? [];

  const tabs = [
    { value: "org", label: copy.tabOrg, visible: canWriteOrg },
    { value: "profile", label: copy.tabProfile, visible: true },
    { value: "security", label: copy.tabSecurity, visible: true },
    {
      value: "report-types",
      label: copy.tabReportTypes,
      visible: canManageLookups,
    },
  ].filter((entry) => entry.visible);

  return (
    <div className="mx-auto w-full max-w-console px-6 py-8">
      <PageHeader eyebrow={nav.groups.system} title={copy.title} lead={copy.lead} />

      {!canWriteOrg ? <p className="mt-5 text-xs text-n6">{copy.readOnlyNote}</p> : null}

      <div className="mt-6 flex flex-wrap gap-1 border-b border-n3">
        {tabs.map((entry) => (
          <Link
            key={entry.value}
            to={`${BASE_PATH}${buildQuery(params, { tab: entry.value, status: undefined, q: undefined, page: undefined })}`}
            aria-current={tab === entry.value ? "page" : undefined}
            className={cn(
              "-mb-px border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors duration-200 ease-brand",
              tab === entry.value
                ? "border-orange text-n9"
                : "border-transparent text-n6 hover:text-n8",
            )}
          >
            {entry.label}
          </Link>
        ))}
      </div>

      <div className="mt-7">
        {tab === "org" && canWriteOrg ? (
          <>
            <h2 className="text-sm font-bold text-n9">{copy.orgTitle}</h2>
            <p className="mt-1 max-w-[64ch] text-xs leading-snug text-n6">{copy.orgLead}</p>
            <div className="mt-5">
              <OrgSettingsForm
                values={{
                  timeZone: settings.timeZone,
                  defaultMapLat: settings.defaultMapLat,
                  defaultMapLng: settings.defaultMapLng,
                  defaultGeofencePolicy: settings.defaultGeofencePolicy,
                  staleSignalMinutes: settings.staleSignalMinutes,
                  locationRetentionDays: settings.locationRetentionDays,
                  inviteExpiryHours: settings.inviteExpiryHours,
                  minPasswordLength: settings.minPasswordLength,
                  notifyOnGeofenceBreach: settings.notifyOnGeofenceBreach,
                  maxActiveEmployees:
                    settings.maxActiveEmployees === null
                      ? ""
                      : String(settings.maxActiveEmployees),
                  maxSupervisors:
                    settings.maxSupervisors == null ? "" : String(settings.maxSupervisors),
                  maxGuards: settings.maxGuards == null ? "" : String(settings.maxGuards),
                  maxAreas: settings.maxAreas == null ? "" : String(settings.maxAreas),
                }}
              />
            </div>
          </>
        ) : null}

        {tab === "profile" ? (
          <>
            <h2 className="text-sm font-bold text-n9">{copy.profileTitle}</h2>
            <div className="mt-5">
              <ProfileForm
                values={{
                  name: profile?.name ?? session.user.name,
                  phone: profile?.phone ?? "",
                  email: profile?.email ?? session.user.email,
                  numeralSystem: profile?.numeralSystem ?? session.user.numeralSystem,
                }}
              />
            </div>
          </>
        ) : null}

        {tab === "security" ? (
          <>
            <h2 className="sr-only">{copy.securityTitle}</h2>
            <SecurityPanel
              sessions={sessions.map((entry) => ({
                id: entry.id,
                ip: entry.ip,
                userAgent: entry.userAgent,
                lastSeenAt: entry.lastSeenAt.toISOString(),
                createdAt: entry.createdAt.toISOString(),
              }))}
              currentSessionId={session.sessionId}
              numerals={session.user.numeralSystem}
              timeZone={settings.timeZone}
            />
          </>
        ) : null}

        {tab === "report-types" && canManageLookups ? <IncidentTypesPage embedded /> : null}
      </div>
    </div>
  );
}
